(() => {
  'use strict';
  const root = document.querySelector('[data-calvielle-care]');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const api = 'https://calvielle-care.smfwzc.workers.dev';
  const storageKey = 'calvielle.care.session.v1';
  const el = name => root.querySelector(`[data-care-${name}]`);
  const copy = JSON.parse(el('copy')?.textContent || '{}');
  const fields = Object.fromEntries(['presence','availability','welcome','welcome-label','start','start-panel','chat','messages','queue','composer','send','join','end','restart','feedback'].map(name => [name, el(name)]));
  const contextDetails = el('context');
  const contextOptIn = el('context-opt-in');
  const contextField = name => root.querySelector(`[data-care-context-field="${name}"]`);
  const input = root.querySelector('textarea');
  const panel = el('panel'), launcher = el('launcher'), closeButton = el('close');
  const thread = el('thread'), options = el('options'), optionsToggle = el('options-toggle'), expandButton = el('expand'), backdrop = el('backdrop');
  const mobile = matchMedia('(max-width: 749px)');
  const background = new Map();
  let scrollLock = null;
  let resizeFollowEnd = false;
  let returnFocus = launcher, closeTimer;
  let credentials = null, current = null, available = false, reachable = false, busy = false, polling = false;
  let pending = null, stopped = false;
  let checkingStatus = null;
  let endedCountdown = null, startReveal = null;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const rendered = new Set();
  const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if (saved && uuid.test(saved.sessionId) && typeof saved.token === 'string' && saved.token.length <= 256 && Date.parse(saved.expiresAt) > Date.now()) credentials = saved;
    else sessionStorage.removeItem(storageKey);
  } catch { /* A denied storage permission must not prevent an in-memory chat. */ }

  function formatCopy(name, token, value) { return copy[name].split(token).join(String(value)); }
  function feedback(text = '') { fields.feedback.textContent = text; }
  function setOptions(open, focus = false) {
    options.hidden = !open;
    optionsToggle.setAttribute('aria-expanded', String(open));
    if (focus) {
      const target = open ? options : optionsToggle;
      target?.focus();
    }
  }
  function fitInput() {
    if (fields.composer.hidden) return;
    input.style.height = '40px';
    input.style.height = `${Math.min(112, Math.max(40, input.scrollHeight))}px`;
  }
  function syncViewport() {
    const viewport = window.visualViewport;
    root.style.setProperty('--care-viewport-height', `${viewport?.height || window.innerHeight}px`);
    root.style.setProperty('--care-viewport-top', `${viewport?.offsetTop || 0}px`);
  }
  function syncModal() {
    const modal = mobile.matches && !panel.hidden;
    panel.setAttribute('aria-modal', String(modal));
    backdrop.hidden = !modal;
    if (modal && !scrollLock) {
      scrollLock = [document.documentElement.style.overflow, document.body.style.overflow];
      document.documentElement.style.overflow = 'hidden'; document.body.style.overflow = 'hidden';
      // Inert only siblings outside this widget, preserving any pre-existing state.
      for (let node = root; node.parentElement; node = node.parentElement) {
        for (const sibling of node.parentElement.children) {
          if (sibling === node || ['SCRIPT','STYLE','LINK','HEAD'].includes(sibling.tagName)) continue;
          background.set(sibling, sibling.inert); sibling.inert = true;
        }
        if (node.parentElement === document.body) break;
      }
    } else if (!modal && scrollLock) {
      for (const [node, inert] of background) node.inert = inert;
      background.clear();
      [document.documentElement.style.overflow, document.body.style.overflow] = scrollLock;
      scrollLock = null;
    }
  }
  function openPanel(trigger = launcher) {
    clearTimeout(closeTimer); panel.classList.remove('is-closing');
    returnFocus = trigger; panel.hidden = false; launcher.setAttribute('aria-expanded','true');
    root.classList.add('is-open'); syncViewport(); syncModal(); fitInput();
    closeButton.focus(); refresh();
  }
  function closePanel() {
    setOptions(false);
    launcher.setAttribute('aria-expanded','false');
    const finish = () => { panel.hidden = true; panel.classList.remove('is-closing'); root.classList.remove('is-open'); syncModal(); (returnFocus?.isConnected ? returnFocus : launcher).focus(); };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else { panel.classList.add('is-closing'); closeTimer = setTimeout(finish,200); }
  }
  function remember() {
    try { if (credentials) sessionStorage.setItem(storageKey, JSON.stringify(credentials)); else sessionStorage.removeItem(storageKey); } catch { /* Session remains in memory only. */ }
  }
  function readVisitorContext() {
    // Opening the details or typing into a field does not authorize disclosure.
    // No URL, product handle, customer identity, or cart data is inferred.
    if (!contextOptIn?.checked) return null;
    const context = {};
    for (const name of ['name','email','orderReference','category']) {
      const value = contextField(name)?.value.normalize('NFC').trim().replace(/\s+/gu, ' ');
      if (value) context[name] = value;
    }
    if (!Object.keys(context).length) return null;
    if (context.name && (context.name.length > 80 || !/^[\p{L}\p{M}][\p{L}\p{M} .'-]*$/u.test(context.name))) throw new Error(copy.context_invalid);
    if (context.email && (context.email.length > 254 || !/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/.test(context.email))) throw new Error(copy.context_invalid);
    if (context.orderReference && (context.orderReference.length > 64 || !/^[A-Za-z0-9#_-]+$/.test(context.orderReference) || /^\d{13,19}$/.test(context.orderReference))) throw new Error(copy.context_invalid);
    if (context.category && !['product','sizing','order','delivery','returns','other'].includes(context.category)) throw new Error(copy.context_invalid);
    return context;
  }
  function clearVisitorContext() {
    if (!contextOptIn || !contextDetails) return;
    contextOptIn.checked = false;
    for (const name of ['name','email','orderReference','category']) { const field = contextField(name); if (field) field.value = ''; }
    contextDetails.open = false;
  }
  function cancelEndedCountdown() {
    if (!endedCountdown) return;
    const previous = endedCountdown; endedCountdown = null;
    clearTimeout(previous.timer);
    previous.animations.forEach(animation => animation.cancel());
  }
  function resetConversation({animate = false} = {}) {
    cancelEndedCountdown(); startReveal?.cancel(); startReveal = null;
    const restoreFocus = !panel.hidden && !panel.classList.contains('is-closing') && panel.contains(document.activeElement);
    credentials = null; current = null; pending = null; remember(); rendered.clear(); fields.messages.replaceChildren();
    clearVisitorContext();
    input.value = ''; setOptions(false); fields.queue.textContent = ''; delete fields.queue.dataset.ended;
    fields.chat.hidden = true; fields['start-panel'].hidden = false; feedback(); controls();
    thread.scrollTop = 0;
    if (animate && !panel.hidden && !panel.classList.contains('is-closing') && !reducedMotion.matches && thread.animate) {
      startReveal = thread.animate([{opacity:0,transform:'translateY(6px)'},{opacity:1,transform:'translateY(0)'}], {duration:360,easing:'cubic-bezier(.22,1,.36,1)'});
    }
    if (restoreFocus) fields.start.focus({preventScroll:true});
  }
  function returnToStart(countdown) {
    if (endedCountdown !== countdown || current?.session.status !== 'closed') return;
    if (panel.hidden || panel.classList.contains('is-closing') || document.hidden || reducedMotion.matches || !thread.animate) {
      resetConversation(); return;
    }
    countdown.animations = [
      thread.animate([{opacity:1,transform:'translateY(0)'},{opacity:0,transform:'translateY(-4px)'}], {duration:180,easing:'ease-out',fill:'forwards'}),
      fields.queue.animate([{opacity:1},{opacity:0}], {duration:180,easing:'ease-out',fill:'forwards'})
    ];
    Promise.all(countdown.animations.map(animation => animation.finished)).then(() => {
      if (endedCountdown === countdown && current?.session.status === 'closed') resetConversation({animate:true});
    }).catch(() => { /* Manual restart or reduced motion can cancel this transition. */ });
  }
  function syncEndedTimer() {
    if (current?.session.status !== 'closed') { cancelEndedCountdown(); delete fields.queue.dataset.ended; return; }
    if (endedCountdown) return;
    const notice = document.createElement('span'); notice.textContent = copy.conversation_ended;
    const label = document.createElement('span'); label.className = 'cv-care__countdown'; label.setAttribute('aria-hidden','true');
    const number = document.createElement('span'); number.className = 'cv-care__countdown-number'; number.textContent = '3';
    const [beforeNumber, afterNumber = ''] = copy.return_countdown.split('__CV_SECONDS__');
    label.append(beforeNumber,number,afterNumber);
    // Announce the destination once, without repeating the live region every second.
    const announcement = document.createElement('span'); announcement.className = 'cv-care__sr-only'; announcement.textContent = ' ' + formatCopy('return_announcement','__CV_SECONDS__',3);
    fields.queue.replaceChildren(notice,label,announcement); fields.queue.dataset.ended = 'true';
    const countdown = {deadline:performance.now() + 3000,timer:null,animations:[]};
    endedCountdown = countdown;
    const tick = () => {
      if (endedCountdown !== countdown || current?.session.status !== 'closed') return;
      const remaining = countdown.deadline - performance.now();
      if (remaining <= 0) { returnToStart(countdown); return; }
      number.textContent = String(Math.ceil(remaining / 1000));
      countdown.timer = setTimeout(tick,Math.min(remaining,1000));
    };
    tick();
  }
  function controls() {
    root.dataset.reachable = String(reachable);
    const closed = current?.session.status === 'closed';
    fields.start.disabled = busy || !reachable;
    fields.send.disabled = busy || !current || closed || !reachable || !input.value.trim();
    input.disabled = !current || closed || !reachable;
    input.readOnly = busy;
    fields.join.hidden = !current || ['queued','active','closed'].includes(current.session.status);
    fields.join.disabled = busy || !available;
    fields.end.hidden = !current || closed;
    fields.end.disabled = busy;
    fields.restart.hidden = !closed;
    fields.restart.disabled = busy;
    fields.composer.hidden = !current || !!closed;
    fields.queue.hidden = !current || current.session.status === 'assistant';
    if (current && reachable) fields.availability.hidden = true;
    if (fields.welcome) fields.welcome.hidden = !!current && (current.messages.length > 0 || current.session.status !== 'assistant');
    fields.start.textContent = busy && !current ? copy.connecting : copy.start;
    fields.send.setAttribute('aria-label', busy ? copy.sending : copy.send);
    fields.send.dataset.busy = String(busy);
    fields.composer.setAttribute('aria-busy', String(busy));
    el('working').hidden = !busy || !current;
    el('retry').hidden = reachable || busy || polling;
    el('shortcuts').hidden = !!current && (current.messages.length > 0 || current.session.status !== 'assistant');
    root.querySelectorAll('[data-care-prompt]').forEach(button => { button.disabled = busy || !reachable; });
  }
  const errorMessages = {
    offline:copy.error_offline,
    human_offline:copy.error_offline,
    queue_full:copy.error_queue_full,
    rate_limited:copy.error_rate_limited,
    session_expired:copy.error_session_expired,
    invalid_session:copy.error_invalid_session,
    conflict:copy.error_conflict,
    feature_disabled:copy.error_feature_disabled
  };
  async function request(path, body, authenticated = false) {
    const requestCredentials = authenticated ? credentials : null;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 22000);
    try {
      const headers = { Accept:'application/json' };
      if (body !== undefined) headers['Content-Type'] = 'application/json';
      if (authenticated) {
        if (!credentials) throw new Error(copy.error_missing_session);
        headers.Authorization = `Bearer ${credentials.token}`;
      }
      const response = await fetch(api + path, { method: body === undefined ? 'GET' : 'POST', headers, body: body === undefined ? undefined : JSON.stringify(body), signal:controller.signal, credentials:'omit', cache:'no-store', redirect:'error' });
      const data = await response.json();
      if (!response.ok) {
        if (authenticated && credentials === requestCredentials && [401,403,404,410].includes(response.status)) {
          credentials = null; remember();
          if (current) { current.session.status = 'closed'; render(current); }
        }
        throw new Error(errorMessages[data?.error?.code] || copy.error_request);
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError' || error instanceof TypeError) throw new Error(copy.error_connection);
      throw error;
    } finally { clearTimeout(timeout); }
  }
  function safeLink(value) {
    try {
      const url = new URL(value);
      if (url.username || url.password || url.protocol !== 'https:' || url.port || url.search) return null;
      if (url.origin === 'https://calvielle.com' && /^\/(?:$|collections\/(?:all|bracelets|the-elemental-five)$|pages\/contact$|policies\/(?:shipping-policy|refund-policy|privacy-policy|terms-of-service)$)/.test(url.pathname)) return url.href;
      if (url.origin === 'https://account.calvielle.com' && /^\/account\/?$/.test(url.pathname)) return url.href;
    } catch { /* Only approved navigation, never arbitrary model URLs. */ }
    return null;
  }
  function render(snapshot) {
    if (!snapshot?.session || !Array.isArray(snapshot.messages)) throw new Error(copy.error_snapshot);
    current = snapshot;
    fields['start-panel'].hidden = true;
    fields.chat.hidden = false;
    const session = snapshot.session;
    if (session.status === 'queued') {
      const position = Number.isInteger(session.queuePosition) && session.queuePosition > 0 ? formatCopy('queue_position','__CV_POSITION__',session.queuePosition) + ' ' : '';
      const estimate = Number.isFinite(session.estimatedWaitMinutes) && session.estimatedWaitMinutes > 0 ? formatCopy(Math.ceil(session.estimatedWaitMinutes) === 1 ? 'queue_wait_one' : 'queue_wait_other','__CV_MINUTES__',Math.ceil(session.estimatedWaitMinutes)) : copy.queue_estimate_pending;
      fields.queue.textContent = session.agentOnline ? position + estimate : copy.queue_away;
    } else if (session.status === 'active') fields.queue.textContent = session.agentOnline ? copy.queue_active : copy.queue_connection_lost;
    else if (session.status !== 'closed') fields.queue.textContent = '';
    syncEndedTimer();
    const messages = fields.messages;
    const scroller = thread;
    const atEnd = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 100;
    let added = false;
    for (const item of snapshot.messages.slice(0,100)) {
      if (!item.id || rendered.has(item.id)) continue;
      rendered.add(item.id); added = true;
      const row = document.createElement('li'); row.className = 'cv-care__message';
      const role = ['visitor','assistant','agent','system'].includes(item.role) ? item.role : 'system';
      row.dataset.role = role;
      const label = document.createElement('p'); label.className = 'cv-care__message-label';
      label.textContent = {visitor:copy.role_visitor,assistant:copy.role_assistant,agent:copy.role_agent,system:copy.role_system}[role];
      const avatar = document.createElement('span'); avatar.className = 'cv-care__avatar'; avatar.dataset.kind = role; avatar.setAttribute('aria-hidden','true');
      if (role === 'visitor') {
        const svg = document.createElementNS('http://www.w3.org/2000/svg','svg');
        svg.setAttribute('viewBox','0 0 24 24'); svg.setAttribute('width','18'); svg.setAttribute('height','18'); svg.setAttribute('fill','none');
        const path = document.createElementNS('http://www.w3.org/2000/svg','path');
        path.setAttribute('d','M15.5 8a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5.5 20v-1.5a6.5 6.5 0 0 1 13 0V20');
        path.setAttribute('stroke','currentColor'); path.setAttribute('stroke-width','1.2'); path.setAttribute('stroke-linecap','round'); svg.append(path); avatar.append(svg);
      } else if (role === 'assistant') {
        const brand = el('brand-avatar');
        if (brand) avatar.append(brand.content.cloneNode(true));
      } else if (role === 'agent') {
        const svg = document.createElementNS('http://www.w3.org/2000/svg','svg');
        svg.setAttribute('viewBox','0 0 24 24'); svg.setAttribute('width','22'); svg.setAttribute('height','22'); svg.setAttribute('fill','none');
        const path = document.createElementNS('http://www.w3.org/2000/svg','path');
        path.setAttribute('d','M5 13v-2a7 7 0 0 1 14 0v2M7 17H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h2v5Zm10-5h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-2v-5Zm1 5v1a2 2 0 0 1-2 2h-3M15.5 11a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM8 20a5 5 0 0 1 4-2');
        path.setAttribute('stroke','currentColor'); path.setAttribute('stroke-width','1.2'); path.setAttribute('stroke-linecap','round'); path.setAttribute('stroke-linejoin','round'); svg.append(path); avatar.append(svg);
      } else avatar.textContent = 'i';
      const content = document.createElement('div'); content.className = 'cv-care__message-content';
      const text = document.createElement('p'); text.className = 'cv-care__message-text'; text.textContent = String(item.text || '').slice(0,4000);
      content.append(label,text); row.append(avatar,content);
      if (Array.isArray(item.links)) {
        const links = document.createElement('div'); links.className = 'cv-care__message-links';
        for (const itemLink of item.links.slice(0,4)) {
          const href = safeLink(itemLink.url); if (!href) continue;
          const link = document.createElement('a'); link.className = 'cv-care__link'; link.href = href; link.textContent = String(itemLink.label || copy.learn_more).slice(0,80); links.append(link);
        }
        content.append(links);
      }
      messages.append(row);
    }
    controls();
    fitInput();
    if (added && atEnd) requestAnimationFrame(() => { scroller.scrollTop = scroller.scrollHeight; });
  }
  async function readStatus() {
    try {
      const data = await request('/api/public/status');
      available = data.humanOnline === true && data.queueOpen === true;
      reachable = data.aiAvailable === true || available;
      fields.presence.textContent = data.humanOnline === true ? copy.online : copy.offline;
      fields.presence.dataset.online = String(data.humanOnline === true);
      fields.availability.textContent = available ? copy.availability_queue : reachable ? copy.availability_ai : copy.availability_unavailable;
      fields.availability.hidden = !!current && reachable;
      if (fields['welcome-label']) fields['welcome-label'].textContent = data.aiAvailable === true ? copy.role_assistant : copy.role_agent;
      el('handoff-status').textContent = available ? copy.handoff_online : copy.handoff_offline;
      document.querySelectorAll('[data-care-page-status]').forEach(node => { node.textContent = available ? copy.page_online : reachable ? copy.page_ai : copy.page_unavailable; });
    } catch {
      available = false; reachable = false;
      fields.presence.textContent = copy.unreachable; fields.presence.dataset.online = 'false';
      fields.availability.hidden = false;
      fields.availability.textContent = copy.availability_email;
      el('handoff-status').textContent = copy.handoff_unavailable;
      document.querySelectorAll('[data-care-page-status]').forEach(node => { node.textContent = copy.page_unavailable; });
    }
    controls();
  }
  function status() {
    if (!checkingStatus) checkingStatus = readStatus().finally(() => { checkingStatus = null; });
    return checkingStatus;
  }
  async function refresh() {
    if (busy || polling || stopped || document.hidden || panel.hidden) return;
    polling = true;
    try {
      await status();
      const readCredentials = credentials;
      if (readCredentials && current?.session.status !== 'closed') {
        const snapshot = await request(`/api/public/sessions/${readCredentials.sessionId}`, undefined, true);
        if (credentials === readCredentials && current?.session.status !== 'closed') render(snapshot);
      }
    } catch (error) { feedback(error.message); }
    finally { polling = false; controls(); }
  }
  async function start() {
    if (busy || !reachable) return;
    startReveal?.cancel(); startReveal = null;
    busy = true; feedback(); controls();
    try {
      const context = readVisitorContext();
      credentials = await request('/api/public/sessions', context ? {consent:true,context} : {consent:true}); remember();
      clearVisitorContext();
      rendered.clear(); fields.messages.replaceChildren();
      render(await request(`/api/public/sessions/${credentials.sessionId}`, undefined, true));
      if (!panel.hidden) input.focus();
    } catch (error) { feedback(error.message); }
    finally { busy = false; controls(); if (!panel.hidden && current?.session.status === 'assistant') input.focus(); }
  }
  async function mutate(action, payload = {}) {
    if (busy || !credentials) return false;
    busy = true; feedback(); controls();
    const fingerprint = action + JSON.stringify(payload);
    if (pending?.fingerprint !== fingerprint) pending = {fingerprint,requestId:crypto.randomUUID()};
    try {
      const result = await request(`/api/public/sessions/${credentials.sessionId}/${action}`, {...payload,requestId:pending.requestId}, true);
      pending = null; render(result); return true;
    } catch (error) { feedback(error.message); return false; }
    finally { busy = false; controls(); }
  }
  fields.start.addEventListener('click',start);
  el('retry').addEventListener('click', async () => { feedback(); await refresh(); });
  root.querySelectorAll('[data-care-prompt]').forEach(button => button.addEventListener('click', async () => {
    if (busy || !reachable) return;
    const prompt = button.dataset.carePrompt;
    if (!current) await start();
    if (!current || current.session.status === 'closed') return;
    input.value = prompt; fitInput(); controls(); input.focus();
  }));
  root.querySelectorAll('[data-care-disclosure]').forEach(button => button.addEventListener('click',() => {
    const details = button.closest('.cv-care__disclosure')?.querySelector('[data-care-disclosure-panel]');
    if (!details) return;
    const expanded = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded',String(expanded));
    details.setAttribute('aria-hidden',String(!expanded)); details.inert = !expanded;
  }));
  launcher.addEventListener('click',() => { if (panel.hidden || panel.classList.contains('is-closing')) openPanel(); else closePanel(); });
  closeButton.addEventListener('click',closePanel);
  backdrop.addEventListener('click', closePanel);
  optionsToggle.addEventListener('click', () => setOptions(options.hidden, true));
  expandButton.addEventListener('click', () => {
    resizeFollowEnd = thread.scrollHeight - thread.scrollTop - thread.clientHeight < 100;
    const expanded = panel.classList.toggle('is-expanded');
    expandButton.setAttribute('aria-pressed', String(expanded));
    expandButton.setAttribute('aria-label', expanded ? copy.restore_size : copy.expand_size);
    fitInput();
  });
  panel.addEventListener('transitionend', event => {
    if (event.target !== panel || !['width','height'].includes(event.propertyName)) return;
    fitInput();
    if (resizeFollowEnd) thread.scrollTop = thread.scrollHeight;
  });
  root.addEventListener('keydown',event => {
    if (panel.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation();
      if (!options.hidden) setOptions(false, true); else closePanel();
    }
    if (event.key === 'Tab' && mobile.matches) {
      const focusable = [...panel.querySelectorAll('button,a[href],textarea,[tabindex="0"]')].filter(item => !item.disabled && !item.closest('[inert]') && item.getClientRects().length);
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  });
  document.addEventListener('click',event => {
    const trigger = event.target.closest?.('[data-care-open]');
    if (trigger) { event.preventDefault(); openPanel(trigger); }
    if (!options.hidden && !options.contains(event.target) && !optionsToggle.contains(event.target)) setOptions(false);
    // Close the mobile overlay before native in-page email navigation.
    const email = event.target.closest?.('[data-care-email]');
    if (email && root.contains(email)) closePanel();
  });
  input.addEventListener('input', () => { fitInput(); controls(); });
  input.addEventListener('keydown', event => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && event.keyCode !== 229 && !mobile.matches) {
      event.preventDefault();
      if (!fields.send.disabled) fields.composer.requestSubmit();
    }
  });
  fields.composer.addEventListener('submit',async event => {
    event.preventDefault();
    const text = input.value.trim(); if (!text || !current || fields.send.disabled) return;
    thread.scrollTop = thread.scrollHeight;
    if (await mutate('messages',{text,mode:current.session.status === 'assistant' ? 'assistant' : 'human'})) input.value = '';
    else feedback(`${copy.send_failed} ${fields.feedback.textContent}`);
    fitInput(); controls();
    if (!panel.hidden) input.focus();
  });
  fields.join.addEventListener('click',async () => { if (available) { setOptions(false); await mutate('join'); if (!panel.hidden) input.focus(); } });
  fields.end.addEventListener('click',async () => { setOptions(false); await mutate('leave'); if (!panel.hidden) optionsToggle.focus(); });
  fields.restart.addEventListener('click',() => resetConversation({animate:true}));
  reducedMotion.addEventListener('change',() => {
    if (!reducedMotion.matches) return;
    startReveal?.cancel(); startReveal = null;
    if (endedCountdown?.animations.length) resetConversation();
  });
  mobile.addEventListener('change', () => { syncModal(); fitInput(); });
  window.addEventListener('resize', () => { syncViewport(); fitInput(); });
  window.visualViewport?.addEventListener('resize', syncViewport);
  window.visualViewport?.addEventListener('scroll', syncViewport);
  document.addEventListener('visibilitychange',() => { if (!document.hidden) refresh(); });
  window.addEventListener('pagehide',() => { stopped = true; });
  window.addEventListener('pageshow',() => { stopped = false; refresh(); });
  setInterval(() => { if (!document.hidden && !stopped && !panel.hidden) refresh(); }, 20000);
  refresh();
  if (document.querySelector('[data-care-page-status]')) status();
})();
